/* ============================================================
   FERAL 2026 — PDP interactions (fp-)
   ------------------------------------------------------------
   Variant swap: pills update the [name=id] hidden input, swap
   the visible price + sale chip + CTA state, push variant id
   into the URL, and scroll the matching media into view.
   Qty stepper: standard +/-, clamped to min 1.
   Sticky mobile bar: revealed once primary CTA is scrolled past.
   Mobile gallery: scroll-snap dot indicator sync.
   ATB submission: handled by Dawn's <product-form> wrapper.
   ============================================================ */
(function () {
  'use strict';

  const sections = document.querySelectorAll('[data-fp]');
  if (!sections.length) return;

  /* ── Money formatter (mirrors Shopify GBP default; rendered values
     come from server-formatted strings on first paint, but variant
     swaps need a client-side formatter). ── */
  const SYMBOLS = { GBP: '£', EUR: '€', USD: '$', AUD: 'A$', CAD: 'CA$', NZD: 'NZ$' };
  const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'GBP';
  const sym = SYMBOLS[currency] || (currency + ' ');
  function money(cents) {
    const v = Math.max(0, Math.round(cents)) / 100;
    return sym + v.toFixed(2);
  }

  sections.forEach(initSection);

  function initSection(root) {
    const variantsScript = root.querySelector('[data-fp-variants]');
    let variants = [];
    try { variants = JSON.parse(variantsScript.textContent); } catch (e) { variants = []; }
    if (!variants.length) return;

    const pills = root.querySelectorAll('[data-fp-pill]');
    const variantIdInput = root.querySelector('[data-fp-variant-id]');
    const priceCurrent = root.querySelector('[data-fp-price-current]');
    const priceCompare = root.querySelector('[data-fp-price-compare]');
    const priceChip = root.querySelector('[data-fp-price-chip]');
    const cta = root.querySelector('[data-fp-cta]');
    const ctaLabel = root.querySelector('[data-fp-cta-label]');
    const bisWrap = root.querySelector('[data-fp-bis]');
    const productUrl = root.dataset.url || '';
    const optionGroups = root.querySelectorAll('[data-fp-option-index]');

    /* ── Pill clicks: re-pick variant ── */
    pills.forEach((input) => {
      input.addEventListener('change', () => {
        const optIndex = parseInt(input.dataset.optionIndex, 10);
        const value = input.value;
        // Mark selected on the visible label and clear siblings in the same group
        const groupRoot = input.closest('[data-fp-option-index]');
        if (groupRoot) {
          groupRoot.querySelectorAll('.fp__pill').forEach((l) => l.classList.remove('is-selected'));
          input.closest('.fp__pill').classList.add('is-selected');
          const valueLabel = root.querySelector('[data-fp-variant-value="' + optIndex + '"]');
          if (valueLabel) valueLabel.textContent = value;
        }
        recomputeVariant();
      });
    });

    function currentSelection() {
      const out = [];
      optionGroups.forEach((g, i) => {
        const sel = g.querySelector('.fp__pill.is-selected input[data-fp-pill]');
        out[i] = sel ? sel.value : null;
      });
      return out;
    }

    function findVariant(selection) {
      return variants.find((v) => {
        for (let i = 0; i < selection.length; i++) {
          if (selection[i] != null && v.options[i] !== selection[i]) return false;
        }
        return true;
      });
    }

    /* ── Recompute on pill change ── */
    function recomputeVariant() {
      const sel = currentSelection();
      const v = findVariant(sel);

      // Update which-values-are-available state for sibling option groups
      updatePillAvailability(sel);

      if (!v) {
        setUnavailable();
        return;
      }

      // Hidden form id
      if (variantIdInput) variantIdInput.value = String(v.id);

      // Price line
      if (priceCurrent) priceCurrent.textContent = money(v.price);

      // Klarna installment — divide the variant price by N (3 or 4) and re-render.
      // Same numbers feed the custom Klarna modal: per-installment + total.
      const klarnaWrap = root.querySelector('[data-fp-klarna]');
      const klarnaAmt = root.querySelector('[data-fp-klarna-amt]');
      let klarnaSplits = 3;
      if (klarnaWrap && klarnaAmt) {
        klarnaSplits = parseInt(klarnaWrap.dataset.splits, 10) || 3;
        klarnaAmt.textContent = money(Math.floor(v.price / klarnaSplits));
      }
      // Modal price sync — installment + every "total" cell mirrors the
      // currently-selected variant. Money formatter is shared with the page.
      const klmInstallments = root.querySelectorAll('[data-fp-klm-installment]');
      const klmTotals = root.querySelectorAll('[data-fp-klm-total]');
      const installmentCents = Math.floor(v.price / klarnaSplits);
      klmInstallments.forEach((el) => { el.textContent = money(installmentCents); });
      klmTotals.forEach((el) => { el.textContent = money(v.price); });

      // Compare-at chip + strikethrough — toggle existence
      const onSale = v.compare_at_price && v.compare_at_price > v.price;
      togglePriceCompare(onSale, v.compare_at_price);

      // CTA state — single transforming pill. The button stays interactive in
      // both states; in OOS state, click opens the inline BIS panel instead of
      // submitting the cart form (intercepted in the click handler below).
      const ctaSlot = root.querySelector('[data-fp-cta-slot]');
      if (v.available) {
        cta.disabled = false;
        cta.removeAttribute('aria-disabled');
        cta.classList.remove('is-sold-out');
        if (ctaSlot) ctaSlot.classList.remove('is-sold-out');
        if (ctaLabel) ctaLabel.textContent = 'Add to bag';
        closeBis();
      } else {
        cta.disabled = false; // stay clickable — click opens BIS panel
        cta.removeAttribute('aria-disabled');
        cta.classList.add('is-sold-out');
        if (ctaSlot) ctaSlot.classList.add('is-sold-out');
        if (ctaLabel) ctaLabel.textContent = 'Notify me when back in stock';
        // Reset BIS panel state but keep it collapsed until user clicks CTA.
        resetBisToInitial();
      }

      // URL state — non-destructive replace, helps share-link select correct variant
      if (productUrl && history.replaceState) {
        const u = new URL(window.location.href);
        u.searchParams.set('variant', String(v.id));
        history.replaceState({}, '', u.toString());
      }

      // Sync the matching gallery media (if any)
      if (v.featured_media && v.featured_media.id) {
        const media = root.querySelector('[data-fp-media][data-media-id="' + v.featured_media.id + '"]');
        if (media) {
          const idx = parseInt(media.dataset.index, 10) || 0;
          if (window.matchMedia('(min-width: 990px)').matches) {
            // Desktop: fade-swap via is-active class on media + thumb
            const allMedia = root.querySelectorAll('[data-fp-media]');
            const allThumbs = root.querySelectorAll('[data-fp-thumb]');
            allMedia.forEach((m) => m.classList.toggle('is-active', m === media));
            allThumbs.forEach((t) => t.classList.toggle('is-active', parseInt(t.dataset.index, 10) === idx));
            const thumbsRail = root.querySelector('[data-fp-thumbs]');
            const activeThumb = root.querySelector('[data-fp-thumb].is-active');
            if (thumbsRail && activeThumb) {
              const offset = activeThumb.offsetLeft - thumbsRail.clientWidth / 2 + activeThumb.clientWidth / 2;
              thumbsRail.scrollTo({ left: Math.max(0, offset), behavior: 'smooth' });
            }
          } else {
            // Mobile: snap horizontally
            const track = root.querySelector('[data-fp-gallery-track]');
            if (track) {
              track.scrollTo({ left: idx * track.clientWidth, behavior: 'smooth' });
            }
          }
        }
      }
    }

    function setUnavailable() {
      if (cta) {
        cta.disabled = true;
        cta.setAttribute('aria-disabled', 'true');
        cta.classList.add('is-sold-out');
      }
      if (ctaLabel) ctaLabel.textContent = 'Unavailable';
    }

    function togglePriceCompare(show, compareCents) {
      const row = root.querySelector('[data-fp-price]');
      if (!row) return;
      let compareEl = row.querySelector('[data-fp-price-compare]');
      let chipEl = row.querySelector('[data-fp-price-chip]');
      if (show) {
        if (!compareEl) {
          compareEl = document.createElement('s');
          compareEl.className = 'fp__price-compare';
          compareEl.setAttribute('data-fp-price-compare', '');
          row.appendChild(compareEl);
        }
        compareEl.textContent = money(compareCents);
        if (!chipEl) {
          chipEl = document.createElement('span');
          chipEl.className = 'fp__price-chip';
          chipEl.setAttribute('data-fp-price-chip', '');
          chipEl.textContent = 'Sale';
          row.appendChild(chipEl);
        }
      } else {
        if (compareEl) compareEl.remove();
        if (chipEl) chipEl.remove();
      }
    }

    /* ── Mark unavailable values per option group given current siblings ── */
    function updatePillAvailability(selection) {
      optionGroups.forEach((group, gi) => {
        const labels = group.querySelectorAll('.fp__pill');
        labels.forEach((label) => {
          const input = label.querySelector('input[data-fp-pill]');
          if (!input) return;
          const value = input.value;
          // Build a hypothetical selection holding other groups fixed,
          // varying only this group's value.
          const probe = selection.slice();
          probe[gi] = value;
          const match = variants.find((v) => {
            for (let i = 0; i < probe.length; i++) {
              if (probe[i] != null && v.options[i] !== probe[i]) return false;
            }
            return v.available;
          });
          label.classList.toggle('is-unavailable', !match);
        });
      });
    }

    // Initial availability paint
    updatePillAvailability(currentSelection());

    /* Mobile sticky CTA is now pure CSS (position: sticky on .fp__cta) — no
       observer or clone needed. The same button physically locks into its
       natural slot as the user scrolls. */

    /* ── SIZE-REQUIRED GATE + BOTTOM SHEET ──
       If the user clicks Add to bag without picking a size: open the bottom
       sheet on mobile, show inline red error on desktop. Sheet rows are
       themselves "select + add to bag" actions. */
    const sizeGroup = root.querySelector('[data-fp-is-size]');
    const sheet = root.querySelector('[data-fp-sheet]');
    const sheetClose = root.querySelector('[data-fp-sheet-close]');
    const sheetOverlay = root.querySelector('[data-fp-sheet-overlay]');
    const sheetRows = root.querySelectorAll('[data-fp-sheet-row]');

    function isSizeSelected() {
      if (!sizeGroup) return true;
      return !!sizeGroup.querySelector('.fp__pill.is-selected');
    }

    // Expose to the events layer (feral-events.liquid). Its dynamic-checkout
    // AddToCart fires from a document-capture listener that runs BEFORE this
    // script's element-level size gate, so it consults this to avoid emitting a
    // phantom AddToCart when the buy is still blocked on a missing size.
    try { window.__feral = window.__feral || {}; window.__feral.isVariantSelected = isSizeSelected; } catch (e) { /* swallow */ }

    function openSheet() {
      if (!sheet) return;
      sheet.hidden = false;
      document.documentElement.classList.add('fp-sheet-locked');
      requestAnimationFrame(() => sheet.classList.add('is-open'));
    }
    function closeSheet() {
      if (!sheet) return;
      sheet.classList.remove('is-open');
      document.documentElement.classList.remove('fp-sheet-locked');
      // Collapse any expanded sheet-BIS panels so the next open is clean.
      // Subscribed (success) cells stay locked — they're terminal.
      if (typeof resetSheetBisCellsOnClose === 'function') resetSheetBisCellsOnClose();
      setTimeout(() => { if (sheet && !sheet.classList.contains('is-open')) sheet.hidden = true; }, 380);
    }

    /* Express / dynamic checkout (Shop Pay · Apple Pay) was REMOVED from the PDP
       on 2026-06-19 (it bypassed the basket), so its size-gate is gone too. The
       Add-to-bag size gate below still uses isSizeSelected() / flashSizeError(). */

    function flashSizeError() {
      if (!sizeGroup) return;
      // Toggle off then on so the shake animation re-triggers each time
      sizeGroup.classList.remove('is-error');
      void sizeGroup.offsetWidth;
      sizeGroup.classList.add('is-error');
      sizeGroup.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    function clearSizeError() {
      if (sizeGroup) sizeGroup.classList.remove('is-error');
    }

    if (cta) {
      // Capture phase so we run before the form's submit handler.
      cta.addEventListener('click', (e) => {
        // Sold out → intercept and open the inline BIS panel instead.
        if (cta.classList.contains('is-sold-out')) {
          e.preventDefault();
          e.stopImmediatePropagation();
          // Need a size before we can subscribe to a specific variant's BIS.
          if (!isSizeSelected()) {
            if (window.matchMedia('(max-width: 989px)').matches) {
              openSheet();
            } else {
              flashSizeError();
            }
            return;
          }
          openBis();
          return;
        }
        // In stock + size missing → existing flow.
        if (!isSizeSelected()) {
          e.preventDefault();
          e.stopImmediatePropagation();
          if (window.matchMedia('(max-width: 989px)').matches) {
            openSheet();
          } else {
            flashSizeError();
          }
        }
      }, true);
    }

    /* ── Reset size selection after successful add-to-cart ──
       Customers often buy multiple sizes (e.g. M for themselves, L for a
       friend). Without this reset, the previously-selected size stays
       active, so a second tap on Add to Cart silently adds another of the
       same size instead of prompting again. After every successful cart
       update originating from this PDP, we clear the size pills so the
       next add forces a fresh size pick — the bottom sheet (mobile) or
       inline error (desktop) will re-trigger on the next click.

       Defensive: run inside requestAnimationFrame so the clear lands AFTER
       any synchronous re-render that cart-drawer's renderContents may
       trigger in the same tick. Also strip `?variant=` from the URL so
       reloads / share-links don't re-hydrate the same selection. */
    function clearSizeSelection() {
      if (!sizeGroup) return;
      // Visible state — remove is-selected from every pill label in the group
      sizeGroup.querySelectorAll('.fp__pill').forEach((el) => el.classList.remove('is-selected'));
      // Form state — uncheck every radio in the group (covers both the
      // hidden input inside the pill label AND any tied input element)
      sizeGroup.querySelectorAll('input[data-fp-pill], input[type="radio"]').forEach((p) => {
        p.checked = false;
        p.removeAttribute('checked');
      });
      // URL state — drop the variant param so refresh doesn't re-select
      try {
        var u = new URL(window.location.href);
        if (u.searchParams.has('variant')) {
          u.searchParams.delete('variant');
          window.history.replaceState(null, '', u.toString());
        }
      } catch (e) {}
    }
    if (window.subscribe && window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.cartUpdate) {
      window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, () => {
        // Only clear if this PDP is still in the DOM — guards against stale
        // subscriptions when the page changes via PJAX-style navigation.
        if (!document.body.contains(root)) return;
        // Defer one frame so we land after any sync cart-drawer re-render.
        requestAnimationFrame(clearSizeSelection);
      });
    }

    // Pill change → variant input gets re-enabled (the form needs it submittable)
    // and any error state clears.
    pills.forEach((p) => {
      p.addEventListener('change', () => {
        clearSizeError();
        if (variantIdInput) variantIdInput.removeAttribute('disabled');
      });
    });

    // Sheet rows → in-stock rows: set the matching pill, dispatch change,
    // close sheet, submit form. Out-of-stock rows: expand the inline BIS
    // panel for that size, capture email, subscribe via SEND IT. The user
    // can subscribe to multiple sold-out sizes in one sheet visit.
    let sheetLastSubscribedEmail = '';

    function getVariantForSheetRow(row) {
      const sizeValue = row.dataset.value || '';
      const sizeOptIdx = parseInt(row.dataset.optionIndex, 10);
      if (Number.isNaN(sizeOptIdx)) return null;
      const selection = currentSelection();
      selection[sizeOptIdx] = sizeValue;
      return findVariant(selection) || null;
    }

    function collapseSheetBisCells(exceptCell) {
      root.querySelectorAll('[data-fp-sheet-cell].is-bis-expanded').forEach((c) => {
        if (c !== exceptCell) c.classList.remove('is-bis-expanded');
      });
    }

    sheetRows.forEach((row) => {
      row.addEventListener('click', () => {
        // ── OOS row → inline BIS expand ──
        if (row.dataset.fpSheetRowOos === 'true') {
          const cell = row.closest('[data-fp-sheet-cell]');
          if (!cell) return;
          // Already-subscribed rows are terminal (no toggle).
          if (cell.classList.contains('is-bis-success')) return;
          const wasExpanded = cell.classList.contains('is-bis-expanded');
          collapseSheetBisCells(cell);
          cell.classList.toggle('is-bis-expanded', !wasExpanded);
          row.setAttribute('aria-expanded', String(!wasExpanded));
          if (!wasExpanded) {
            const input = cell.querySelector('[data-fp-sheet-bis-input]');
            const errorEl = cell.querySelector('[data-fp-sheet-bis-error]');
            // Clear any prior error state from a previous failed submit
            if (errorEl) errorEl.hidden = true;
            if (input) input.classList.remove('is-error');
            if (input) {
              // Carry the email across cells: if customer already subscribed
              // to size A and now opens size B, pre-fill with the same address.
              if (sheetLastSubscribedEmail && !input.value) {
                input.value = sheetLastSubscribedEmail;
              }
              setTimeout(() => input.focus({ preventScroll: false }), 220);
            }
          }
          return;
        }

        // ── In-stock row → existing select + submit flow ──
        if (row.disabled) return;
        const value = row.dataset.value || '';
        const optIdx = row.dataset.optionIndex;
        const matching = root.querySelector(
          'input[data-fp-pill][data-option-index="' + optIdx + '"][value="' + value.replace(/"/g, '\\"') + '"]'
        );
        if (matching) {
          matching.checked = true;
          matching.dispatchEvent(new Event('change', { bubbles: true }));
        }
        closeSheet();
        // Submit the standard Add-to-Bag form. (Apple Pay / Shop Pay
        // never reach this branch — they're gated upstream with the
        // inline "pick a size first." hint instead of the sheet.)
        setTimeout(() => {
          if (cta && !cta.disabled) {
            const form = cta.closest('form');
            if (form && typeof form.requestSubmit === 'function') {
              form.requestSubmit(cta);
            } else if (cta.click) {
              cta.click();
            }
          }
        }, 60);
      });
    });

    // Reset sheet BIS cells back to default state every time the sheet
    // closes — protects against a half-typed email + reopen reading as a
    // success row. Subscribed (success) cells are sticky and preserved.
    function resetSheetBisCellsOnClose() {
      root.querySelectorAll('[data-fp-sheet-cell].is-bis-expanded').forEach((c) => {
        c.classList.remove('is-bis-expanded');
        const r = c.querySelector('[data-fp-sheet-row]');
        if (r) r.setAttribute('aria-expanded', 'false');
      });
    }

    if (sheetClose) sheetClose.addEventListener('click', closeSheet);
    if (sheetOverlay) sheetOverlay.addEventListener('click', closeSheet);
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && sheet && !sheet.hidden) closeSheet();
    });

    /* ── BACK-IN-STOCK PANEL ──
       Inline reveal that opens directly under the OOS CTA. Submits to SEND IT's
       /api/subscribe (source 'bis', the variant id), which owns the restock email. */
    const bisFormEl = root.querySelector('[data-fp-bis-form]');
    const bisEmailEl = root.querySelector('[data-fp-bis-email]');
    const bisSubmitEl = root.querySelector('[data-fp-bis-submit]');
    const bisSubmitLabelEl = root.querySelector('[data-fp-bis-submit-label]');
    const bisErrorEl = root.querySelector('[data-fp-bis-error]');
    const bisSuccessEl = root.querySelector('[data-fp-bis-success]');
    const bisSizeEls = root.querySelectorAll('[data-fp-bis-size], [data-fp-bis-success-size]');

    function currentSizeLabel() {
      // The size group is option index 0 (or whatever the size pill group is).
      // Fall back to the first selected pill's value.
      const sel = root.querySelector('.fp__pill.is-selected input[data-fp-pill]');
      return sel && sel.value ? sel.value : '';
    }

    function syncBisSizeLabel() {
      const sz = currentSizeLabel();
      const text = sz ? sz : 'this size';
      bisSizeEls.forEach((el) => { el.textContent = text; });
    }

    function openBis() {
      if (!bisWrap) return;
      syncBisSizeLabel();
      // Reset to initial form state (in case it was previously submitted).
      resetBisToInitial();
      bisWrap.hidden = false;
      // Defer focus past the slide-in transition so it doesn't fight the layout.
      setTimeout(() => { if (bisEmailEl) bisEmailEl.focus({ preventScroll: false }); }, 220);
      // Scroll into view if it would be off-screen.
      requestAnimationFrame(() => {
        const rect = bisWrap.getBoundingClientRect();
        if (rect.bottom > window.innerHeight - 40) {
          bisWrap.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      });
    }

    function closeBis() {
      if (!bisWrap) return;
      bisWrap.hidden = true;
      resetBisToInitial();
    }

    function resetBisToInitial() {
      if (bisFormEl) bisFormEl.hidden = false;
      if (bisSuccessEl) bisSuccessEl.hidden = true;
      if (bisErrorEl) { bisErrorEl.hidden = true; bisErrorEl.textContent = ''; }
      if (bisEmailEl) {
        bisEmailEl.classList.remove('is-error');
        // don't clear the email — user may have typed it before swapping size
      }
      if (bisSubmitEl) bisSubmitEl.disabled = false;
      if (bisSubmitLabelEl) bisSubmitLabelEl.textContent = 'Notify me';
    }

    function showBisError(msg) {
      if (!bisErrorEl) return;
      bisErrorEl.textContent = msg;
      bisErrorEl.hidden = false;
      if (bisEmailEl) bisEmailEl.classList.add('is-error');
    }

    function showBisSuccess() {
      syncBisSizeLabel();
      if (bisFormEl) bisFormEl.hidden = true;
      if (bisSuccessEl) bisSuccessEl.hidden = false;
    }

    /* Shared back-in-stock subscribe call — used by the main inline BIS form
       AND by each per-row BIS panel in the size sheet. Returns
       { ok: true } | { ok: false, error: <human-readable> }. One write, to
       SEND IT, whose answer is what the shopper sees. */
    async function subscribeBis(variantId, email) {
      if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        return { ok: false, error: 'Please enter a valid email.' };
      }
      // 🚨 THE SIZE CHECK MOVED ABOVE THE WRITES (2026-08-21). It used to sit AFTER the
      // feral-mail dual-write, so every "forgot to pick a size" submission wrote a
      // bis_subscribe row with variant_id: null — rows bis_pending() can never match and
      // nobody can ever be notified from. Nothing to subscribe to means nothing to write.
      if (!variantId) {
        return { ok: false, error: 'Pick a size first.' };
      }

      // ══ SEND IT OWNS THE ANSWER (2026-08-21; the only write since 2026-10-19) ══════
      // ⛔ THE CALL THAT DECIDES THE UI MUST BE THE ONE THAT OWNS THE PROMISE.
      // First-party host on purpose: mail.feralclo.com is ours, so ad blockers leave it be.
      const ourPromise = fetch('https://mail.feralclo.com/api/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email, source: 'bis', variant_id: String(variantId), uid: (window.__feral && window.__feral.uid) ? String(window.__feral.uid) : null }),
      }).then((res) => (res.ok ? { ok: true } : { ok: false, error: 'Something glitched. Try again in a sec.' }))
        // Same app, second hostname — a DNS or edge blip on the branded host must not
        // lose a signup now that this call is the one the shopper is waiting on.
        .catch(() =>
          fetch('https://feral-mail.vercel.app/api/subscribe', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email: email, source: 'bis', variant_id: String(variantId), uid: (window.__feral && window.__feral.uid) ? String(window.__feral.uid) : null }),
          }).then((res) => (res.ok ? { ok: true } : { ok: false, error: 'Something glitched. Try again in a sec.' }))
            .catch(() => ({ ok: false, error: 'Network hiccup. Try again.' }))
        );

      return await ourPromise;
    }

    async function submitBis() {
      if (!bisEmailEl) return;
      const email = (bisEmailEl.value || '').trim();
      const selection = currentSelection();
      const v = findVariant(selection);

      bisErrorEl && (bisErrorEl.hidden = true);
      bisEmailEl.classList.remove('is-error');
      if (bisSubmitEl) bisSubmitEl.disabled = true;
      if (bisSubmitLabelEl) bisSubmitLabelEl.textContent = 'Saving…';

      const result = await subscribeBis(v && v.id, email);
      if (result.ok) {
        showBisSuccess();
      } else {
        showBisError(result.error);
        if (bisSubmitEl) bisSubmitEl.disabled = false;
        if (bisSubmitLabelEl) bisSubmitLabelEl.textContent = 'Notify me';
      }
    }

    if (bisFormEl) {
      bisFormEl.addEventListener('submit', (e) => {
        e.preventDefault();
        submitBis();
      });
    }

    /* ── Sheet inline BIS forms ──
       One form per sold-out cell. Submit posts to the same SEND IT
       endpoint as the main BIS form. On success the cell flips to its
       success state (badge turns red w/ tick, panel collapses, row
       becomes non-interactive). Customer can subscribe to multiple
       sizes in one sheet visit. */
    root.querySelectorAll('[data-fp-sheet-bis-form]').forEach((form) => {
      const cell = form.closest('[data-fp-sheet-cell]');
      if (!cell) return;
      const row = cell.querySelector('[data-fp-sheet-row]');
      const input = form.querySelector('[data-fp-sheet-bis-input]');
      const submitBtn = form.querySelector('[data-fp-sheet-bis-submit]');
      const submitLabel = form.querySelector('[data-fp-sheet-bis-submit-label]');
      const errorEl = form.querySelector('[data-fp-sheet-bis-error]');
      const statusText = row ? row.querySelector('[data-fp-sheet-row-status-text]') : null;

      function showError(msg) {
        if (errorEl) { errorEl.textContent = msg; errorEl.hidden = false; }
        if (input) input.classList.add('is-error');
      }
      function clearError() {
        if (errorEl) errorEl.hidden = true;
        if (input) input.classList.remove('is-error');
      }

      if (input) {
        input.addEventListener('input', () => {
          input.classList.remove('is-error');
          if (errorEl && !errorEl.hidden) errorEl.hidden = true;
        });
      }

      form.addEventListener('submit', async (e) => {
        e.preventDefault();
        if (!input || !row) return;
        const email = (input.value || '').trim();
        const variant = getVariantForSheetRow(row);

        clearError();
        if (submitBtn) submitBtn.disabled = true;
        if (submitLabel) submitLabel.textContent = 'Saving…';

        const result = await subscribeBis(variant && variant.id, email);

        if (result.ok) {
          sheetLastSubscribedEmail = email;
          // Flip cell to success state.
          cell.classList.remove('is-bis-expanded');
          cell.classList.add('is-bis-success');
          row.setAttribute('aria-expanded', 'false');
          if (statusText) statusText.textContent = "You're on the list";
        } else {
          showError(result.error);
          if (submitBtn) submitBtn.disabled = false;
          if (submitLabel) submitLabel.textContent = 'Notify me';
        }
      });
    });
    if (bisEmailEl) {
      bisEmailEl.addEventListener('input', () => {
        bisEmailEl.classList.remove('is-error');
        if (bisErrorEl && !bisErrorEl.hidden) bisErrorEl.hidden = true;
      });
    }

    /* ��─ KLARNA "LEARN MORE" MODAL ──
       Custom modal (not redirect to klarna.com). Open on "Learn more" click,
       close on overlay/close-button click, Escape, or programmatic close.
       Body scroll-locked while open. Focus moves to the close button on
       open and back to the trigger on close. */
    const klmRoot = root.querySelector('[data-fp-klarna-modal]');
    const klmOpenTriggers = root.querySelectorAll('[data-fp-klarna-modal-open]');
    const klmCloseEls = klmRoot ? klmRoot.querySelectorAll('[data-fp-klarna-modal-close]') : [];
    let klmLastTrigger = null;

    // Portal the Klarna modal to <body> so its position:fixed/z-index clears the
    // sticky site header. Inside .shopify-section the overlay was z-index-trapped
    // below the header (z-index:90), so the top of the card + close button
    // rendered UNDER the FERAL header. Mirrors the lightbox + size-guide portal
    // pattern already in this theme (gotcha #8b). Listeners are queried from
    // `root` above before the move and survive reparenting.
    if (klmRoot && klmRoot.parentElement !== document.body) {
      document.querySelectorAll('body > [data-fp-klarna-modal]').forEach(function (el) {
        if (el !== klmRoot) el.remove();
      });
      document.body.appendChild(klmRoot);
    }

    function openKlarnaModal(trigger) {
      if (!klmRoot) return;
      klmLastTrigger = trigger || null;
      klmRoot.hidden = false;
      // Force reflow so the transition fires from the initial transformed state.
      void klmRoot.offsetWidth;
      klmRoot.classList.add('is-open');
      document.documentElement.classList.add('fp-klm-locked');
      document.body.classList.add('fp-klm-locked');
      const focusEl = klmRoot.querySelector('.fp__klm-close');
      if (focusEl) focusEl.focus({ preventScroll: true });
    }
    function closeKlarnaModal() {
      if (!klmRoot) return;
      klmRoot.classList.remove('is-open');
      document.documentElement.classList.remove('fp-klm-locked');
      document.body.classList.remove('fp-klm-locked');
      // Hide after the slide-out completes.
      setTimeout(() => {
        if (klmRoot && !klmRoot.classList.contains('is-open')) klmRoot.hidden = true;
        if (klmLastTrigger && typeof klmLastTrigger.focus === 'function') {
          klmLastTrigger.focus({ preventScroll: true });
        }
      }, 320);
    }

    klmOpenTriggers.forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        openKlarnaModal(btn);
      });
    });
    klmCloseEls.forEach((el) => {
      el.addEventListener('click', closeKlarnaModal);
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && klmRoot && klmRoot.classList.contains('is-open')) {
        closeKlarnaModal();
      }
    });

    /* ── SIZE & FIT GUIDE LINK ── */
    // Intercepts the inline link and triggers the FERAL Size Guide modal
    // (rendered by snippets/feral-size-guide.liquid). Falls back to the
    // /pages/fit-guide URL if the snippet didn't render (chart unavailable).
    const sizeGuideLink = root.querySelector('[data-fp-size-guide]');
    if (sizeGuideLink) {
      sizeGuideLink.addEventListener('click', (e) => {
        const trigger = root.querySelector('[data-fsg-open][data-fsg-tab="guide"]');
        if (trigger) {
          e.preventDefault();
          trigger.click();
        }
        // else: anchor href takes over and navigates to /pages/fit-guide
      });
    }

    /* ── GALLERY CONTROLLER ──
       One source of truth for "which image is active". Updates:
         • .fp__media.is-active (desktop fade)
         • .fp__thumb.is-active (desktop thumb ring)
         • .fp__dot.is-active (mobile dots)
         • counter "X / N"
         • mobile scroll position of the swipe track
       Driven by: thumb clicks, dot clicks, prev/next arrows, mobile swipe IO. */
    const track = root.querySelector('[data-fp-gallery-track]');
    const slides = root.querySelectorAll('[data-fp-media]');
    const thumbs = root.querySelectorAll('[data-fp-thumb]');
    const dots = root.querySelectorAll('[data-fp-dot]');
    const counterCurrent = root.querySelector('[data-fp-counter-current]');
    const prevBtn = root.querySelector('[data-fp-prev]');
    const nextBtn = root.querySelector('[data-fp-next]');
    const totalSlides = slides.length;

    let activeIdx = 0;

    function setActive(idx, opts) {
      opts = opts || {};
      if (idx < 0) idx = totalSlides - 1;
      if (idx >= totalSlides) idx = 0;
      activeIdx = idx;
      slides.forEach((m) => m.classList.toggle('is-active', parseInt(m.dataset.index, 10) === idx));
      thumbs.forEach((t) => t.classList.toggle('is-active', parseInt(t.dataset.index, 10) === idx));
      dots.forEach((d) => d.classList.toggle('is-active', parseInt(d.dataset.index, 10) === idx));
      if (counterCurrent) counterCurrent.textContent = String(idx + 1);

      const isMobile = window.matchMedia('(max-width: 989px)').matches;

      // On click of a thumb/arrow on mobile, scroll the swipe track. On
      // desktop, the fade-swap (via is-active class) is the navigation —
      // no page scroll, the user stays put.
      if (opts.scrollGallery && isMobile && track) {
        track.scrollTo({ left: idx * track.clientWidth, behavior: 'smooth' });
      }

      // Always centre the active thumb in the rail (desktop only — mobile hides the rail)
      const thumbsRail = root.querySelector('[data-fp-thumbs]');
      const activeThumb = thumbs[idx];
      if (thumbsRail && activeThumb && !isMobile) {
        const offset = activeThumb.offsetLeft - thumbsRail.clientWidth / 2 + activeThumb.clientWidth / 2;
        thumbsRail.scrollTo({ left: Math.max(0, offset), behavior: 'smooth' });
      }
    }

    if (thumbs.length) {
      thumbs.forEach((t) => {
        t.addEventListener('click', () => setActive(parseInt(t.dataset.index, 10) || 0, { scrollGallery: true }));
      });
    }
    if (dots.length && track) {
      dots.forEach((d) => {
        d.addEventListener('click', () => setActive(parseInt(d.dataset.index, 10) || 0, { scrollGallery: true }));
      });
    }
    if (prevBtn) prevBtn.addEventListener('click', () => setActive(activeIdx - 1, { scrollGallery: true }));
    if (nextBtn) nextBtn.addEventListener('click', () => setActive(activeIdx + 1, { scrollGallery: true }));

    // Mobile swipe → IntersectionObserver keeps state in sync without scroll spam
    if (track && slides.length && 'IntersectionObserver' in window) {
      const ioMobile = new IntersectionObserver((entries) => {
        if (!window.matchMedia('(max-width: 989px)').matches) return;
        entries.forEach((entry) => {
          if (entry.intersectionRatio > 0.6) {
            const idx = parseInt(entry.target.dataset.index, 10) || 0;
            if (idx !== activeIdx) {
              activeIdx = idx;
              dots.forEach((d) => d.classList.toggle('is-active', parseInt(d.dataset.index, 10) === idx));
              if (counterCurrent) counterCurrent.textContent = String(idx + 1);
            }
          }
        });
      }, { root: track, threshold: [0.6] });
      slides.forEach((s) => ioMobile.observe(s));
    }

    // Desktop is single-frame — active state is driven entirely by clicks
    // (thumb, arrow, dot). No scroll observer needed.

    // Keyboard arrows when gallery is focused
    const galleryEl = root.querySelector('[data-fp-gallery]');
    if (galleryEl) {
      galleryEl.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft') { e.preventDefault(); setActive(activeIdx - 1, { scrollMobile: true }); }
        if (e.key === 'ArrowRight') { e.preventDefault(); setActive(activeIdx + 1, { scrollMobile: true }); }
      });
    }

    /* ── IMAGE LIGHTBOX ── */
    const lightbox = root.querySelector('[data-fp-lightbox]');
    const lightboxClose = root.querySelector('[data-fp-lightbox-close]');
    const lightboxScroll = root.querySelector('[data-fp-lightbox-scroll]');
    const lightboxItems = root.querySelectorAll('[data-fp-lightbox-item]');

    // Portal lightbox to <body> so position:fixed anchors to the viewport
    // and z-index clears the sticky header. Inside .shopify-section the
    // overlay was trapped on iOS — site header showed through the top of
    // the zoomed image and the close button rendered off-screen.
    if (lightbox && lightbox.parentElement !== document.body) {
      document.querySelectorAll('body > [data-fp-lightbox]').forEach(function (el) {
        if (el !== lightbox) el.remove();
      });
      document.body.appendChild(lightbox);
    }

    function openLightbox(startIdx) {
      if (!lightbox) return;
      lightbox.hidden = false;
      document.documentElement.classList.add('fp-lightbox-locked');
      requestAnimationFrame(() => lightbox.classList.add('is-open'));
      // Jump scroll to the image they clicked
      if (lightboxScroll && lightboxItems[startIdx]) {
        // Wait a frame so layout is computed
        requestAnimationFrame(() => {
          const target = lightboxItems[startIdx];
          lightboxScroll.scrollTop = target.offsetTop;
        });
      }
    }
    function closeLightbox() {
      if (!lightbox) return;
      lightbox.classList.remove('is-open');
      document.documentElement.classList.remove('fp-lightbox-locked');
      setTimeout(() => { if (lightbox && !lightbox.classList.contains('is-open')) lightbox.hidden = true; }, 320);
    }

    if (lightbox) {
      // Click on any media image opens lightbox at that image's index
      slides.forEach((slide) => {
        const img = slide.querySelector('.fp__media-img, .fp__media-video');
        if (!img) return;
        slide.style.cursor = 'zoom-in';
        slide.addEventListener('click', (e) => {
          // Don't trigger when clicking the arrow buttons (event bubbles up otherwise — they're outside .fp__media though, safe)
          const idx = parseInt(slide.dataset.index, 10) || 0;
          openLightbox(idx);
        });
      });
      if (lightboxClose) lightboxClose.addEventListener('click', (e) => {
        e.stopPropagation();
        closeLightbox();
      });
      // Tap/click anywhere inside the lightbox (image, video, empty space) closes.
      // The close button stops propagation above so it doesn't double-fire.
      lightbox.addEventListener('click', closeLightbox);
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && lightbox && !lightbox.hidden) closeLightbox();
      });
    }

    /* ── MOBILE FLOATING CTA ──
       On mobile only, while the button's natural slot is below the viewport
       bottom (minus a 12px inset), render the button as `position: fixed` at
       that bottom inset. The slot wrapper reserves the button's height so
       there's no layout shift. The toggle moment is engineered so that
       fixed-bottom and natural positions overlap exactly — pixel-seamless
       lock-in. Same DOM element, just two render modes. */
    const ctaSlot = root.querySelector('[data-fp-cta-slot]');
    if (cta && ctaSlot) {
      let floatTicking = false;
      function updateFloating() {
        floatTicking = false;
        if (!window.matchMedia('(max-width: 989px)').matches) {
          cta.classList.remove('is-floating');
          return;
        }
        const slotRect = ctaSlot.getBoundingClientRect();
        const stickyOffset = 12;
        const threshold = window.innerHeight - stickyOffset;
        // Float ONLY as the entrance preview while the slot is still below the
        // fold; once it locks into place, let it scroll away with the page. Do
        // NOT re-float when scrolled past (no `|| slotRect.top < 0`) — Harry
        // rejected the reappearing sticky bar 2026-06-18.
        const shouldFloat = slotRect.bottom > threshold;
        cta.classList.toggle('is-floating', shouldFloat);
      }
      function onScrollFloat() {
        if (floatTicking) return;
        floatTicking = true;
        requestAnimationFrame(updateFloating);
      }
      window.addEventListener('scroll', onScrollFloat, { passive: true });
      window.addEventListener('resize', onScrollFloat);
      updateFloating();
    }

  }

  // ── Review badge: rating pill → smooth-scroll to reviews; share → native share ──
  function initReviewBadge() {
    document.querySelectorAll('[data-fp]').forEach((root) => {
      const scrollBtn = root.querySelector('[data-fp-reviews-scroll]');
      if (scrollBtn) {
        scrollBtn.addEventListener('click', () => {
          const reviews = document.querySelector('[data-fr]');
          if (reviews) reviews.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
      }
      const shareBtn = root.querySelector('[data-fp-share]');
      if (shareBtn) {
        shareBtn.addEventListener('click', async () => {
          let url = location.href;
          try { if (root.dataset.url) url = new URL(root.dataset.url, location.origin).href; } catch (e) {}
          const title = document.title;
          if (navigator.share) {
            try { await navigator.share({ title, url }); } catch (e) { /* user cancelled */ }
            return;
          }
          try {
            if (navigator.clipboard) {
              await navigator.clipboard.writeText(url);
              shareBtn.classList.add('is-copied');
              setTimeout(() => shareBtn.classList.remove('is-copied'), 1600);
            }
          } catch (e) { /* clipboard blocked — no-op */ }
        });
      }
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initReviewBadge);
  else initReviewBadge();
})();
